import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import { useMedia } from 'react-use';
import { FlyToInterpolator } from 'react-map-gl';
import { parse, isBefore } from 'date-fns';
import type { AnyProps, PointFeature } from 'supercluster';
import { MapContainer } from '../MapContainer/MapContainer';
import { MapNavigation } from '../MapNavigation/MapNavigation';
import { StyledWidgetContainer } from './WidgetContainer.styles';
import AppContext from '../../../utils/createContext';
import { MediaQuery } from '../../../styles/mediaQuery';
import { InsetMap } from '../InsetMap/InsetMap';
import type { ViewportType, AirportType, FlightType } from '../../../utils/types';
import { ZoomLevels } from '../../../data/constants';
import { DisabledModal, DisabledModalConnection } from '../../molecules/DisabledModal/Modal';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __swaMapboxKey: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __swaMapboxStyleUrl: string;
  }
}

export function WidgetContainer(): ReactElement {
  const isDesktop = useMedia(MediaQuery.Large);
  const isTablet = useMedia(MediaQuery.Medium);
  const [viewport, setViewport] = useState<ViewportType>({
    longitude: -97.9, // viewport-settings
    latitude: 36.3,
    zoom: isDesktop ? ZoomLevels.OutermostDesktop : ZoomLevels.OutermostMobile,
    width: isDesktop ? '100%' : '100vw',
    height: '100%',
  });
  const [originAirport, setOriginAirport] = useState<AirportType | Record<string, never>>({});
  const [destinationAirport, setDestinationAirport] = useState<AirportType | Record<string, never>>(
    {},
  );
  const [airportsData, setAirportsData] = useState<Array<AirportType>>([]);
  const [points, setPoints] = useState<Array<PointFeature<AnyProps>>>([]);
  const [flightType, setFlightType] = useState<FlightType>('none');
  const [userLocation, setUserLocation] = useState<GeolocationPosition | undefined>();
  const [userLocationError, setUserLocationError] = useState<boolean>(false);
  const [goToHawaii, setGoToHawaii] = useState(false);
  const [nonstopChecked, setNonstopChecked] = useState<boolean>(false);
  const [mapFailed, setMapFailed] = useState(false);
  const [showDisabledModal, setShowDisabledModal] = useState(false);
  const [showDisabledModalConnection, setShowDisabledModalConnection] = useState(false);
  const [hawaiiCount, setHawaiiCount] = useState<Array<AirportType>>([]);

  // eslint-disable-next-line no-underscore-dangle
  const [mapboxKey] = useState(window.__swaMapboxKey);
  // eslint-disable-next-line no-underscore-dangle
  const [mapboxStyleUrl] = useState(window.__swaMapboxStyleUrl);

  const parseAirportsData = (data: { [index: string]: AirportType } | ArrayLike<AirportType>) =>
    Object.entries(data).map(([, airportData]) => airportData);

  useEffect(() => {
    if (!originAirport.iata_code && !destinationAirport.iata_code) {
      setNonstopChecked(false);
    }
  }, [originAirport, destinationAirport]);

  useEffect(() => {
    async function fetchAirports() {
      const data = await fetch('/swa-resources/generated/route_map/map_view.json')
        .then((response) => response.json())
        .then((array) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          parseAirportsData(array).map((item: any) => ({
            ...item,
            operational: item.operate_date
              ? isBefore(parse(item.operate_date, 'yyyyMMdd', new Date()), Date.now())
              : true,
          })),
        )
        .catch((error) => console.log(error)); // eslint-disable-line no-console
      if (data) {
        setAirportsData(data);
        // setHawaiiCount(data.reduce((n, x) => n + (x.federal_unit === 'HI'), 0));
        const hawaiiStations: Array<AirportType> = [];
        // eslint-disable-next-line array-callback-return
        data.map((item) => {
          if (item.federal_unit === 'HI') {
            hawaiiStations.push(item);
          }
        });

        // setHawaiiCount(data.filter((item) => item.federal_unit === 'HI'));
        setHawaiiCount(hawaiiStations);
      }
    }
    fetchAirports();
  }, []);

  useEffect(() => {
    if (goToHawaii) {
      setViewport({
        ...viewport,
        longitude: -156.30340425996235,
        latitude: 20.580330248477598,
        zoom: ZoomLevels.Innermost,
        transitionInterpolator: new FlyToInterpolator({ speed: 1 }),
        transitionDuration: 'auto',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToHawaii]);

  const { Provider } = AppContext;

  return (
    <Provider
      value={{
        viewport,
        setViewport,
        airportsData,
        points,
        setPoints,
        originAirport,
        setOriginAirport,
        destinationAirport,
        setDestinationAirport,
        flightType,
        setFlightType,
        userLocation,
        setUserLocation,
        userLocationError,
        setUserLocationError,
        goToHawaii,
        setGoToHawaii,
        isDesktop,
        nonstopChecked,
        setNonstopChecked,
        mapboxKey,
        mapboxStyleUrl,
        mapFailed,
        setMapFailed,
        showDisabledModal,
        setShowDisabledModal,
        showDisabledModalConnection,
        setShowDisabledModalConnection,
        hawaiiCount,
      }}
    >
      <StyledWidgetContainer data-reverse id="widget-container">
        <MapNavigation />
        <MapContainer />
        {isTablet &&
          viewport.zoom < ZoomLevels.Innermost &&
          !mapFailed &&
          viewport.longitude > -150 && <InsetMap data-reverse />}
        {showDisabledModal && <DisabledModal onClose={() => setShowDisabledModal(false)} />}
        {showDisabledModalConnection && (
          <DisabledModalConnection onClose={() => setShowDisabledModalConnection(false)} />
        )}
      </StyledWidgetContainer>
    </Provider>
  );
}
